import { useCallback, useEffect, useState } from 'react';
import { ref, get, set, update, remove, onValue } from 'firebase/database';
import { database } from '../firebase';
import { toast } from '../utils/toast';
import { sanitizeKey } from './useSession';

// The facilitator's private case library, keyed by their identity key.
export default function useArchives(identityKey, active) {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!active || !identityKey) return undefined;
    const unsubscribe = onValue(
      ref(database, `archives/${identityKey}`),
      (snap) => {
        const list = Object.entries(snap.val() || {})
          // The same node also holds the facilitator's saved cases.
          .filter(([, a]) => a?.kind !== 'case')
          .map(([id, a]) => ({ id, ...a }))
          .sort((a, b) => (b.archivedAt || 0) - (a.archivedAt || 0));
        setArchives(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsubscribe;
  }, [identityKey, active]);

  const removeArchive = useCallback(
    (archiveId) => remove(ref(database, `archives/${identityKey}/${archiveId}`)).catch(() => {
      toast('Could not delete the archive.');
    }),
    [identityKey]
  );

  /**
   * Creates a new session from an archive.
   * mode 'template': row skeleton + case stages (all un-revealed) + settings, 
   *                  no student items/votes, ready to teach the case again.
   * mode 'full':     everything, for a read-write resurrection of the session.
   * The cloner becomes the new session's facilitator.
   */
  const cloneArchive = useCallback(
    async (archive, newSessionId, mode = 'template') => {
      const id = sanitizeKey(newSessionId);
      if (!id) throw new Error('Enter a session ID for the new session.');
      const metaSnap = await get(ref(database, `sessions/${id}/meta`));
      if (metaSnap.exists()) throw new Error(`Session "${id}" already exists, pick another ID.`);

      const src = archive.data || {};
      const now = Date.now();
      // Claim the session (facilitator) first, then fill in the rest, the
      // settings/case write rules check meta.facilitatorKey.
      await set(ref(database, `sessions/${id}/meta`), {
        createdAt: now,
        lastUpdated: now,
        facilitatorKey: identityKey,
        facilitatorName: src.meta?.facilitatorName || 'Facilitator',
      });

      const paths = {};
      if (src.settings) paths[`sessions/${id}/settings`] = src.settings;
      if (src.rows) paths[`sessions/${id}/rows`] = src.rows;
      if (src.diffRows) paths[`sessions/${id}/diffRows`] = src.diffRows;
      if (src.case?.stages) {
        const stages = {};
        Object.entries(src.case.stages).forEach(([k, s]) => {
          const { revealedAt, ...rest } = s;
          stages[k] = mode === 'template' ? rest : s; // template: hide all stages again
        });
        paths[`sessions/${id}/case/stages`] = stages;
      }
      if (mode === 'full') {
        if (src.items) paths[`sessions/${id}/items`] = src.items;
        if (src.votes) paths[`sessions/${id}/votes`] = src.votes;
        if (src.probabilities) paths[`sessions/${id}/probabilities`] = src.probabilities;
        if (src.snapshots) paths[`sessions/${id}/snapshots`] = src.snapshots;
      }
      if (Object.keys(paths).length) await update(ref(database), paths);
      return id;
    },
    [identityKey]
  );

  return { archives, loading, removeArchive, cloneArchive };
}
